import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MIGRATION_FIELDS,
  MIGRATION_KIND_LABELS,
  MIGRATION_LIMITS,
  type ColumnMapping,
  type MigrationBatchDto,
  type MigrationBatchSummary,
  type MigrationFileDto,
  type MigrationKind,
  type MigrationProfileDto,
  type MigrationReconciliationResponse,
  type MigrationReportResponse,
} from '@hesabat/contracts';
import { migrationApi, migrationBase, ProblemError, UnauthorizedError } from './api';
import { Icon } from './icons';
import Loading from './Loading';
import MigrationReport from './MigrationReport';
import MigrationReview from './MigrationReview';
import { formatMoney } from './money';
import Wizard, { useWizardDraft, type WizardStep } from './Wizard';
import './migration.css';
import Hint from './Hint';

/**
 * مركز ترحيل البيانات: شركة قادمة من برنامج سابق (الأمين، QuickBooks، Odoo، Excel…) ترفع
 * ملفاتها، وتطابق أعمدتها، وتراجع صفوفها وتصلحها، ثم تطابقها محاسبيًا وتعتمدها دفعة
 * واحدة. لا شىء يمس الدفاتر قبل الاعتماد، والدفعة المعتمدة تُعكس كاملة ما لم يُبنَ عليها.
 *
 * نفس الشاشة يفتحها مشغّل المنصة لشركة عميل كخدمة (`asPlatform`) — والتدقيق باسمه.
 */
const KINDS: readonly MigrationKind[] = [
  'accounts',
  'trial_balance',
  'customers',
  'suppliers',
  'items',
];

const KIND_HINTS: Record<MigrationKind, string> = {
  accounts: 'دليل البرنامج القديم ليُطابق على دليلك — يكفى لو كانت أكواد الميزان مختلفة.',
  trial_balance: 'أرصدة كل الحسابات فى تاريخ البداية. أساس المطابقة كلها.',
  customers: 'بيانات العملاء وأرصدتهم المستحقة.',
  suppliers: 'بيانات الموردين والمستحق لهم.',
  items: 'الأصناف بوحداتها وأسعارها، وكميات وتكلفة المخزون الافتتاحى.',
};

const STEPS: readonly WizardStep[] = [
  {
    id: 'kind',
    title: 'اختر ما سترحّله',
    hint: 'دفعة واحدة تحمل ملفاتك كلها بتاريخ بداية العمل على البرنامج.',
    icon: 'sparkle',
  },
  {
    id: 'upload',
    title: 'ارفع الملفات',
    hint: 'Excel (xlsx أو xls القديم) أو CSV كما صدّرها البرنامج القديم — تُقرأ على الخادم ولا تُحفظ.',
    icon: 'plus',
  },
  {
    id: 'map',
    title: 'طابق الأعمدة',
    hint: 'اقترحنا لكل عمود معناه — أكّد أو غيّر، واحفظ المطابقة لملفات نفس البرنامج.',
    icon: 'reconcile',
  },
  {
    id: 'review',
    title: 'راجع وعدّل',
    hint: 'كل صف عليه مشكلة ملوّن ومعه إصلاح بضغطة. لا شىء يُرحَّل قبل الاعتماد.',
    icon: 'journal',
  },
  {
    id: 'reconcile',
    title: 'المطابقة المحاسبية',
    hint: 'الميزان متزن، والعملاء والموردون والمخزون تساوى حساباتها — شرط الاعتماد.',
    icon: 'chart',
  },
  {
    id: 'commit',
    title: 'اعتمد',
    hint: 'قيد واحد وحركات مخزون وبيانات أساسية فى معاملة واحدة، وتقرير يُطبع.',
    icon: 'check',
  },
];

const STATUS: Record<MigrationBatchSummary['status'], { label: string; tone: string }> = {
  draft: { label: 'مسودة', tone: 'warn' },
  committed: { label: 'معتمدة', tone: '' },
  reversed: { label: 'معكوسة', tone: 'bad' },
};

interface Draft {
  batchId: string | null;
  step: number;
  kinds: MigrationKind[];
  confirmed: string[];
}

interface Props {
  companyId: string;
  /** مشغّل المنصة يعمل للشركة كخدمة. */
  asPlatform?: boolean;
  companyName?: string;
  onSessionLost: () => void;
  onBack?: () => void;
}

function today(): string {
  return new Date().getFullYear() + '-01-01';
}

function problemText(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const issues = caught.problem.issues ?? [];
    return [caught.problem.detail ?? caught.problem.title, ...issues.map((issue) => issue.message)]
      .filter(Boolean)
      .join(' — ');
  }
  return caught instanceof Error ? caught.message : String(caught);
}

export default function DataMigration({
  companyId,
  asPlatform = false,
  companyName,
  onSessionLost,
  onBack,
}: Props) {
  const base = migrationBase(companyId, asPlatform);
  const [draft, setDraft, clearDraft, savedAt] = useWizardDraft<Draft>(
    'hesabat:migration:' + (asPlatform ? 'platform:' : '') + companyId,
    {
      batchId: null,
      step: 0,
      kinds: ['trial_balance', 'customers', 'suppliers', 'items'],
      confirmed: [],
    },
  );
  const [batches, setBatches] = useState<MigrationBatchSummary[] | null>(null);
  const [batch, setBatch] = useState<MigrationBatchDto | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [report, setReport] = useState<MigrationReportResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setFailure(problemText(caught));
    },
    [onSessionLost],
  );

  const loadBatches = useCallback(() => {
    migrationApi.batches(base).then(setBatches).catch(fail);
  }, [base, fail]);

  const loadBatch = useCallback(
    async (batchId: string) => {
      try {
        const loaded = await migrationApi.batch(base, batchId);
        setBatch(loaded);
        return loaded;
      } catch (caught) {
        // دفعة حُذفت أو اعتُمدت من مكان آخر: المسودة المحلية لا تُستأنف
        if (caught instanceof ProblemError && caught.problem.status === 404) clearDraft();
        else fail(caught);
        return null;
      }
    },
    [base, clearDraft, fail],
  );

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // مسودة محفوظة فى المتصفح: يُستأنف المعالج من حيث توقف
  useEffect(() => {
    if (draft.batchId && !batch) {
      void loadBatch(draft.batchId).then((loaded) => {
        if (loaded?.status === 'draft') setWizardOpen(true);
      });
    }
    // مرة عند الفتح فقط: المسودة تُقرأ من المتصفح ولا تتغير بعده إلا من المعالج نفسه
  }, []);

  async function openReport(batchId: string): Promise<void> {
    setFailure(null);
    try {
      setReport(await migrationApi.report(base, batchId));
    } catch (caught) {
      fail(caught);
    }
  }

  if (report) {
    return (
      <MigrationReport
        report={report}
        companyName={companyName}
        onBack={() => {
          setReport(null);
          loadBatches();
        }}
      />
    );
  }

  if (wizardOpen) {
    return (
      <MigrationWizard
        base={base}
        draft={draft}
        setDraft={setDraft}
        savedAt={savedAt}
        batch={batch}
        setBatch={setBatch}
        reloadBatch={loadBatch}
        onCancel={() => {
          setWizardOpen(false);
          loadBatches();
        }}
        onCommitted={(committed) => {
          clearDraft();
          setBatch(null);
          setWizardOpen(false);
          setReport(committed);
          loadBatches();
        }}
        onSessionLost={onSessionLost}
      />
    );
  }

  return (
    <>
      {asPlatform && (
        <p className="notice">
          <Icon name="platform" size={18} />
          <span>
            تعمل كمشغّل منصة على ترحيل بيانات {companyName ?? 'الشركة'} كخدمة — كل خطوة تُسجَّل
            باسمك فى سجل تدقيق الشركة.
          </span>
        </p>
      )}
      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>دفعات الترحيل</h2>
            <p className="sub">
              انقل بياناتك من برنامجك السابق: دليل الحسابات، الميزان الافتتاحى، العملاء والموردون
              وأرصدتهم، والأصناف ومخزونها.
            </p>
          </div>
          <div className="panel-actions">
            {onBack && (
              <button type="button" className="ghost" onClick={onBack}>
                رجوع
              </button>
            )}
            <button
              type="button"
              className="primary"
              onClick={() => {
                clearDraft();
                setBatch(null);
                setFailure(null);
                setWizardOpen(true);
              }}
            >
              <Icon name="plus" size={16} /> دفعة جديدة
            </button>
          </div>
        </div>
        {failure && <p className="error">{failure}</p>}
        {batches === null ? (
          <Loading />
        ) : batches.length === 0 ? (
          <div className="mig-empty">
            <Icon name="sparkle" size={28} />
            <p>
              لا دفعات بعد. ابدأ دفعة، وارفع ملفات Excel كما هى من البرنامج القديم — البرنامج يتعرف
              على الأعمدة ويكشف المكرر والأخطاء قبل أن يُرحَّل شىء.
            </p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="mig-batches">
              <thead>
                <tr>
                  <th>الدفعة</th>
                  <th>البيان</th>
                  <th>تاريخ البداية</th>
                  <th>الحالة</th>
                  <th>الصفوف</th>
                  <th>القيد</th>
                  <th aria-label="إجراءات" />
                </tr>
              </thead>
              <tbody>
                {batches.map((item) => (
                  <BatchRow
                    key={item.id}
                    item={item}
                    busy={busy}
                    onOpen={() => {
                      setFailure(null);
                      setDraft((current) => ({
                        ...current,
                        batchId: item.id,
                        step: Math.max(current.step, 1),
                      }));
                      void loadBatch(item.id).then((loaded) => loaded && setWizardOpen(true));
                    }}
                    onReport={() => void openReport(item.id)}
                    onDelete={async () => {
                      if (!window.confirm('حذف المسودة ' + item.number + ' بملفاتها؟')) return;
                      setBusy(true);
                      try {
                        await migrationApi.deleteBatch(base, item.id);
                        if (draft.batchId === item.id) clearDraft();
                        loadBatches();
                      } catch (caught) {
                        fail(caught);
                      } finally {
                        setBusy(false);
                      }
                    }}
                    onReverse={async (reason) => {
                      setBusy(true);
                      setFailure(null);
                      try {
                        await migrationApi.reverse(
                          base,
                          item.id,
                          'reverse-' + item.id + '-' + Date.now(),
                          reason,
                        );
                        loadBatches();
                        return true;
                      } catch (caught) {
                        fail(caught);
                        return false;
                      } finally {
                        setBusy(false);
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function BatchRow({
  item,
  busy,
  onOpen,
  onReport,
  onDelete,
  onReverse,
}: {
  item: MigrationBatchSummary;
  busy: boolean;
  onOpen: () => void;
  onReport: () => void;
  onDelete: () => void;
  onReverse: (reason: string) => Promise<boolean>;
}) {
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState('');
  const status = STATUS[item.status];
  return (
    <>
      <tr>
        <td>
          <strong>{item.number}</strong>
          {item.operator === 'platform' && <span className="pill mig-operator">مشغّل المنصة</span>}
        </td>
        <td>
          {item.title}
          {item.sourceSystem && <span className="sub"> — من {item.sourceSystem}</span>}
        </td>
        <td className="mig-ltr">{item.cutoverDate}</td>
        <td>
          <span className={'pill ' + status.tone}>{status.label}</span>
        </td>
        <td className="num-cell">{item.rows}</td>
        <td className="mig-ltr">
          {item.journalNumber ?? '—'}
          {item.reversalJournalNumber && (
            <span className="sub"> ↩ {item.reversalJournalNumber}</span>
          )}
        </td>
        <td className="mig-actions">
          {item.status === 'draft' && (
            <>
              <button type="button" className="primary" onClick={onOpen} disabled={busy}>
                متابعة
              </button>
              <button type="button" className="link" onClick={onDelete} disabled={busy}>
                حذف
              </button>
            </>
          )}
          {item.status !== 'draft' && (
            <button type="button" className="ghost" onClick={onReport} disabled={busy}>
              التقرير
            </button>
          )}
          {item.status === 'committed' && (
            <button
              type="button"
              className="link"
              onClick={() => setReversing(!reversing)}
              disabled={busy}
            >
              عكس الدفعة
            </button>
          )}
        </td>
      </tr>
      {reversing && (
        <tr className="mig-reverse-row">
          <td colSpan={7}>
            <form
              className="form-row"
              onSubmit={(event) => {
                event.preventDefault();
                void onReverse(reason.trim()).then((done) => done && setReversing(false));
              }}
            >
              <label className="grow">
                سبب العكس (يُكتب فى سجل التدقيق وفى قيد العكس)
                <input
                  value={reason}
                  maxLength={500}
                  onChange={(event) => setReason(event.target.value)}
                />
                <Hint>
                  العكس يلغى أثر الدفعة كاملة بقيد عكسى — لا يمسح شيئًا، والتاريخ يبقى مسجّلاً.
                </Hint>
              </label>
              <p className="sub grow">
                يُعكس القيد وحركات المخزون كاملة، ويُوقف ما أنشأته الدفعة من عملاء وموردين وأصناف
                وحسابات. يُرفض إن كانت عليها فواتير أو حركات لاحقة.
              </p>
              <div className="form-actions">
                <button
                  type="submit"
                  className="primary"
                  disabled={busy || reason.trim().length < 3}
                >
                  اعكس {item.number}
                </button>
                <button type="button" className="ghost" onClick={() => setReversing(false)}>
                  إلغاء
                </button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// المعالج
// ---------------------------------------------------------------------------

function MigrationWizard({
  base,
  draft,
  setDraft,
  savedAt,
  batch,
  setBatch,
  reloadBatch,
  onCancel,
  onCommitted,
  onSessionLost,
}: {
  base: string;
  draft: Draft;
  setDraft: (update: (current: Draft) => Draft) => void;
  savedAt: Date | null;
  batch: MigrationBatchDto | null;
  setBatch: (batch: MigrationBatchDto | null) => void;
  reloadBatch: (batchId: string) => Promise<MigrationBatchDto | null>;
  onCancel: () => void;
  onCommitted: (report: MigrationReportResponse) => void;
  onSessionLost: () => void;
}) {
  const [title, setTitle] = useState('ترحيل البيانات الافتتاحية');
  const [cutoverDate, setCutoverDate] = useState(today());
  const [sourceSystem, setSourceSystem] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reconciliation, setReconciliation] = useState<MigrationReconciliationResponse | null>(
    null,
  );
  const [focus, setFocus] = useState<{ kind: MigrationKind; rowId: string } | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  // الملفات الأصلية فى الذاكرة لإعادة الرفع بورقة أو صف عناوين آخر — لا تُحفظ فى المتصفح
  const files = useRef(new Map<string, File>());

  const fail = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setFailure(problemText(caught));
    },
    [onSessionLost],
  );

  const step = Math.min(draft.step, STEPS.length - 1);
  const stepId = STEPS[step]!.id;
  const fileList = batch?.files ?? [];
  const errors = batch?.counts.reduce((total, count) => total + count.errors, 0) ?? 0;

  const refresh = useCallback(async () => {
    if (draft.batchId) await reloadBatch(draft.batchId);
  }, [draft.batchId, reloadBatch]);

  useEffect(() => {
    if (stepId === 'reconcile' || stepId === 'commit') {
      if (!draft.batchId) return;
      setReconciliation(null);
      migrationApi.reconciliation(base, draft.batchId).then(setReconciliation).catch(fail);
    }
  }, [stepId, draft.batchId, base, fail]);

  const completed = new Set<string>();
  if (batch) completed.add('kind');
  if (fileList.length > 0) completed.add('upload');
  if (fileList.length > 0 && fileList.every((file) => draft.confirmed.includes(file.id)))
    completed.add('map');
  if (fileList.length > 0 && errors === 0) completed.add('review');
  if (reconciliation?.ready) completed.add('reconcile');

  const goTo = (index: number) => {
    setFailure(null);
    setDraft((current) => ({ ...current, step: index }));
  };

  async function next(): Promise<boolean> {
    setFailure(null);
    if (stepId === 'kind' && !batch) {
      setBusy(true);
      try {
        const created = await migrationApi.createBatch(base, {
          title: title.trim(),
          cutoverDate,
          sourceSystem: sourceSystem.trim() || null,
        });
        setBatch(created);
        setDraft((current) => ({ ...current, batchId: created.id, confirmed: [] }));
      } catch (caught) {
        fail(caught);
        return false;
      } finally {
        setBusy(false);
      }
    }
    if (stepId === 'review') await refresh();
    return true;
  }

  async function commit(): Promise<void> {
    if (!draft.batchId) return;
    setBusy(true);
    setFailure(null);
    try {
      const report = await migrationApi.commit(base, draft.batchId, 'commit-' + draft.batchId);
      onCommitted(report);
    } catch (caught) {
      fail(caught);
      migrationApi
        .reconciliation(base, draft.batchId)
        .then(setReconciliation)
        .catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  const canNext =
    stepId === 'kind'
      ? Boolean(batch) ||
        (title.trim().length >= 2 &&
          /^\d{4}-\d{2}-\d{2}$/.test(cutoverDate) &&
          draft.kinds.length > 0)
      : stepId === 'upload'
        ? fileList.length > 0
        : stepId === 'reconcile'
          ? Boolean(reconciliation?.ready)
          : stepId === 'commit'
            ? Boolean(reconciliation?.ready) && acknowledged
            : true;

  return (
    <Wizard
      title={batch ? 'دفعة ' + batch.number + ' — ' + batch.title : 'دفعة ترحيل جديدة'}
      intro={
        batch ? (
          <>
            تاريخ البداية <span className="mig-ltr">{batch.cutoverDate}</span>
            {batch.sourceSystem ? ' — من ' + batch.sourceSystem : ''}.{' '}
            <button type="button" className="link" onClick={onCancel}>
              كل الدفعات
            </button>
          </>
        ) : (
          <button type="button" className="link" onClick={onCancel}>
            رجوع إلى الدفعات
          </button>
        )
      }
      steps={STEPS}
      current={step}
      onStep={goTo}
      completed={completed}
      canNext={canNext}
      onNext={next}
      onFinish={() => void commit()}
      finishLabel="اعتمد الدفعة"
      busy={busy}
      savedAt={savedAt}
    >
      {failure && (
        <p className="error" role="alert">
          {failure}
        </p>
      )}

      {stepId === 'kind' && (
        <div className="mig-step">
          {!batch && (
            <div className="form-row">
              <label className="grow">
                اسم الدفعة
                <input
                  value={title}
                  maxLength={120}
                  onChange={(event) => setTitle(event.target.value)}
                />
                <Hint>اسم يميز هذه المجموعة من الملفات، مثل «أرصدة نهاية ٢٠٢٥».</Hint>
              </label>
              <label>
                تاريخ بداية العمل على البرنامج
                <input
                  type="date"
                  value={cutoverDate}
                  onChange={(event) => setCutoverDate(event.target.value)}
                />
                <Hint>
                  اليوم الذى تبدأ فيه التسجيل على هذا البرنامج. ما قبله يدخل كأرصدة افتتاحية.
                </Hint>
              </label>
              <label>
                البرنامج السابق
                <input
                  value={sourceSystem}
                  list="mig-sources"
                  maxLength={60}
                  placeholder="الأمين، QuickBooks، Excel…"
                  onChange={(event) => setSourceSystem(event.target.value)}
                />
                <datalist id="mig-sources">
                  {['الأمين', 'QuickBooks', 'Odoo', 'دفترة', 'Sage', 'Excel'].map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                <Hint>أين كانت بياناتك: Excel، دفتر، أو اسم البرنامج القديم.</Hint>
              </label>
            </div>
          )}
          <p className="sub">
            الأرصدة المرحَّلة هى أرصدة نهاية اليوم السابق لتاريخ البداية. أول السنة المالية هو
            الأبسط؛ وفى منتصف السنة تُنقل إيرادات ومصروفات السنة حتى التاريخ لتكتمل قائمة الدخل.
          </p>
          <div className="mig-kinds">
            {KINDS.map((kind) => {
              const chosen = draft.kinds.includes(kind);
              return (
                <label key={kind} className={'mig-kind' + (chosen ? ' chosen' : '')}>
                  <input
                    type="checkbox"
                    checked={chosen}
                    onChange={() =>
                      setDraft((current) => ({
                        ...current,
                        kinds: chosen
                          ? current.kinds.filter((item) => item !== kind)
                          : [...current.kinds, kind],
                      }))
                    }
                  />
                  <span>
                    <strong>{MIGRATION_KIND_LABELS[kind]}</strong>
                    <span className="sub">{KIND_HINTS[kind]}</span>
                    <button
                      type="button"
                      className="link"
                      onClick={(event) => {
                        event.preventDefault();
                        migrationApi
                          .downloadTemplate(
                            base,
                            kind,
                            'قالب ' + MIGRATION_KIND_LABELS[kind] + '.xlsx',
                          )
                          .catch(fail);
                      }}
                    >
                      تنزيل قالب Excel فارغ
                    </button>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {stepId === 'upload' && batch && (
        <UploadStep
          base={base}
          batch={batch}
          kinds={draft.kinds}
          files={files.current}
          onChanged={refresh}
          onError={fail}
        />
      )}

      {stepId === 'map' && batch && (
        <div className="mig-step">
          {fileList.length === 0 && <p className="sub">ارفع ملفًا أولاً.</p>}
          {fileList.map((file) => (
            <MappingCard
              key={file.id + file.headerRow}
              base={base}
              batchId={batch.id}
              file={file}
              confirmed={draft.confirmed.includes(file.id)}
              onConfirmed={async () => {
                setDraft((current) => ({
                  ...current,
                  confirmed: [...new Set([...current.confirmed, file.id])],
                }));
                await refresh();
              }}
              onError={fail}
            />
          ))}
        </div>
      )}

      {stepId === 'review' && batch && (
        <MigrationReview
          base={base}
          batchId={batch.id}
          counts={batch.counts}
          focus={focus}
          onChanged={refresh}
          onError={fail}
        />
      )}

      {stepId === 'reconcile' && (
        <ReconcileStep
          reconciliation={reconciliation}
          onSuspect={(suspect) => {
            setFocus(suspect);
            goTo(STEPS.findIndex((item) => item.id === 'review'));
          }}
        />
      )}

      {stepId === 'commit' && (
        <div className="mig-step">
          {!reconciliation ? (
            <Loading />
          ) : !reconciliation.ready ? (
            <p className="error">المطابقة المحاسبية لم تكتمل — ارجع لخطوة المطابقة.</p>
          ) : (
            <>
              <div className="cards">
                <Card label="حسابات جديدة" value={reconciliation.plan.accountsToCreate} />
                <Card
                  label="عملاء"
                  value={reconciliation.plan.customersToCreate}
                  sub={
                    reconciliation.plan.customersToLink
                      ? '+ ' + reconciliation.plan.customersToLink + ' قائم يُربط'
                      : undefined
                  }
                />
                <Card
                  label="موردون"
                  value={reconciliation.plan.suppliersToCreate}
                  sub={
                    reconciliation.plan.suppliersToLink
                      ? '+ ' + reconciliation.plan.suppliersToLink + ' قائم يُربط'
                      : undefined
                  }
                />
                <Card
                  label="أصناف"
                  value={reconciliation.plan.itemsToCreate}
                  sub={
                    reconciliation.plan.itemsToLink
                      ? '+ ' + reconciliation.plan.itemsToLink + ' قائم يُربط'
                      : undefined
                  }
                />
                <Card label="سطور القيد" value={reconciliation.plan.journalLines} />
                <Card label="أصناف بمخزون" value={reconciliation.plan.stockLines} />
              </div>
              <Totals reconciliation={reconciliation} />
              {reconciliation.plan.openingDifference !== '0.00' && (
                <p className="notice">
                  <Icon name="info" size={18} />
                  <span>
                    الدفعة بلا ميزان: فرق {formatMoney(reconciliation.plan.openingDifference)}{' '}
                    يُقيَّد على حساب فروق الأرصدة الافتتاحية، ويمنع إقفال الفترة حتى يُستكمل
                    الميزان.
                  </span>
                </p>
              )}
              <label className="mig-confirm">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                راجعت الأرقام، وأعتمد ترحيلها بتاريخ {batch?.cutoverDate}. أعرف أن العكس متاح ما لم
                تُبنَ عليها حركات.
              </label>
            </>
          )}
        </div>
      )}
    </Wizard>
  );
}

function Card({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="card">
      <div className="label">{label}</div>
      <div className="num">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

function Totals({ reconciliation }: { reconciliation: MigrationReconciliationResponse }) {
  const totals = reconciliation.totals;
  return (
    <div className="cards">
      <Card label="مدين الميزان" value={formatMoney(totals.trialBalanceDebit)} />
      <Card label="دائن الميزان" value={formatMoney(totals.trialBalanceCredit)} />
      <Card label="أرصدة العملاء" value={formatMoney(totals.receivables)} />
      <Card label="أرصدة الموردين" value={formatMoney(totals.payables)} />
      <Card label="قيمة المخزون" value={formatMoney(totals.inventory)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// الرفع
// ---------------------------------------------------------------------------

function UploadStep({
  base,
  batch,
  kinds,
  files,
  onChanged,
  onError,
}: {
  base: string;
  batch: MigrationBatchDto;
  kinds: readonly MigrationKind[];
  files: Map<string, File>;
  onChanged: () => Promise<void>;
  onError: (caught: unknown) => void;
}) {
  const [profiles, setProfiles] = useState<MigrationProfileDto[]>([]);
  const [busyKind, setBusyKind] = useState<MigrationKind | null>(null);
  const [encoding, setEncoding] = useState<Record<string, 'utf-8' | 'windows-1256' | ''>>({});
  const [profile, setProfile] = useState<Record<string, string>>({});

  useEffect(() => {
    migrationApi.profiles(base).then(setProfiles).catch(onError);
  }, [base, onError]);

  async function upload(
    kind: MigrationKind,
    file: File,
    extra: { sheet?: string; headerRow?: number } = {},
    replace?: string,
  ) {
    if (file.size > MIGRATION_LIMITS.maxFileBytes) {
      onError(
        new Error('الملف أكبر من ' + MIGRATION_LIMITS.maxFileBytes / 1024 / 1024 + ' ميجابايت.'),
      );
      return;
    }
    setBusyKind(kind);
    try {
      if (replace) await migrationApi.deleteFile(base, batch.id, replace);
      const uploaded = await migrationApi.upload(base, batch.id, file, {
        kind,
        ...extra,
        encoding: encoding[kind] || undefined,
        profileId: profile[kind] || undefined,
      });
      files.set(uploaded.id, file);
      await onChanged();
    } catch (caught) {
      onError(caught);
      await onChanged();
    } finally {
      setBusyKind(null);
    }
  }

  const shown = [...new Set([...kinds, ...batch.files.map((file) => file.kind)])];
  return (
    <div className="mig-step">
      {shown.map((kind) => {
        const kindFiles = batch.files.filter((file) => file.kind === kind);
        const kindProfiles = profiles.filter((item) => item.kind === kind);
        return (
          <section key={kind} className="mig-upload">
            <header>
              <h4>{MIGRATION_KIND_LABELS[kind]}</h4>
              <span className="sub">{KIND_HINTS[kind]}</span>
            </header>
            <div className="form-row">
              <label className="file-field grow">
                {busyKind === kind ? 'جارى القراءة…' : 'اختر ملف Excel أو CSV'}
                <input
                  type="file"
                  accept={MIGRATION_LIMITS.extensions.join(',')}
                  disabled={busyKind !== null}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void upload(kind, file);
                  }}
                />
              </label>
              {kindProfiles.length > 0 && (
                <label>
                  ملف مصدر محفوظ
                  <select
                    value={profile[kind] ?? ''}
                    onChange={(event) => setProfile({ ...profile, [kind]: event.target.value })}
                  >
                    <option value="">اقتراح آلى</option>
                    {kindProfiles.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <Hint>لو رفعت ملفًا بنفس الشكل من قبل، اختره فتُربط الأعمدة تلقائيًا.</Hint>
                </label>
              )}
              <label>
                ترميز CSV
                <select
                  value={encoding[kind] ?? ''}
                  onChange={(event) =>
                    setEncoding({
                      ...encoding,
                      [kind]: event.target.value as 'utf-8' | 'windows-1256' | '',
                    })
                  }
                >
                  <option value="">اكتشاف تلقائى</option>
                  <option value="utf-8">UTF-8</option>
                  <option value="windows-1256">عربى ويندوز (1256)</option>
                </select>
                <Hint>اتركه «اكتشاف تلقائى». غيّره فقط لو ظهرت الحروف العربية رموزًا غريبة.</Hint>
              </label>
            </div>
            {kindFiles.map((file) => {
              const original = files.get(file.id);
              return (
                <div key={file.id} className="mig-file">
                  <Icon name="check" size={16} />
                  <div className="grow">
                    <strong>{file.fileName}</strong>{' '}
                    <span className="sub">
                      {file.format.toUpperCase()}
                      {file.sheetName ? ' — ورقة «' + file.sheetName + '»' : ''}
                      {file.encoding ? ' — ' + file.encoding : ''} — {file.rowCount} صف
                    </span>
                  </div>
                  {file.sheets.length > 1 && (
                    <label className="inline-field">
                      الورقة
                      <select
                        value={file.sheetName ?? ''}
                        disabled={!original || busyKind !== null}
                        title={original ? undefined : 'أعد اختيار الملف لتغيير الورقة'}
                        onChange={(event) =>
                          original &&
                          void upload(kind, original, { sheet: event.target.value }, file.id)
                        }
                      >
                        {file.sheets.map((sheet) => (
                          <option key={sheet} value={sheet}>
                            {sheet}
                          </option>
                        ))}
                      </select>
                      <Hint>لو ملف Excel فيه أكثر من ورقة، اختر الورقة التى فيها البيانات.</Hint>
                    </label>
                  )}
                  <label className="inline-field">
                    صف العناوين
                    <select
                      value={file.headerRow}
                      disabled={!original || busyKind !== null}
                      title={original ? undefined : 'أعد اختيار الملف لتغيير صف العناوين'}
                      onChange={(event) =>
                        original &&
                        void upload(
                          kind,
                          original,
                          {
                            sheet: file.sheetName ?? undefined,
                            headerRow: Number(event.target.value),
                          },
                          file.id,
                        )
                      }
                    >
                      {file.topRows.map((row) => (
                        <option key={row.number} value={row.number}>
                          {row.number}:{' '}
                          {row.cells.filter(Boolean).slice(0, 3).join(' | ').slice(0, 40)}
                        </option>
                      ))}
                    </select>
                    <Hint>الصف الذى فيه أسماء الأعمدة (مثل: الاسم، الرصيد) — لا أول صف أرقام.</Hint>
                  </label>
                  <button
                    type="button"
                    className="link"
                    disabled={busyKind !== null}
                    onClick={async () => {
                      try {
                        await migrationApi.deleteFile(base, batch.id, file.id);
                        await onChanged();
                      } catch (caught) {
                        onError(caught);
                      }
                    }}
                  >
                    حذف
                  </button>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// مطابقة الأعمدة
// ---------------------------------------------------------------------------

function MappingCard({
  base,
  batchId,
  file,
  confirmed,
  onConfirmed,
  onError,
}: {
  base: string;
  batchId: string;
  file: MigrationFileDto;
  confirmed: boolean;
  onConfirmed: () => Promise<void>;
  onError: (caught: unknown) => void;
}) {
  const [mapping, setMapping] = useState<ColumnMapping>(file.mapping);
  const [profileName, setProfileName] = useState('');
  const [busy, setBusy] = useState(false);
  const fields = MIGRATION_FIELDS[file.kind];
  const score = new Map(file.suggestions.map((item) => [item.field, item.score]));
  const missing = fields.filter((field) => field.required && (mapping[field.key] ?? null) === null);
  const changed = JSON.stringify(mapping) !== JSON.stringify(file.mapping);

  async function confirm(): Promise<void> {
    if (
      changed &&
      !window.confirm(
        'تغيير المطابقة يعيد قراءة كل صفوف الملف — وتُفقد التعديلات اليدوية عليها. متابعة؟',
      )
    )
      return;
    setBusy(true);
    try {
      await migrationApi.updateMapping(base, batchId, file.id, {
        mapping,
        saveProfileAs: profileName.trim() || null,
      });
      await onConfirmed();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={'mig-map' + (confirmed ? ' confirmed' : '')}>
      <header>
        <h4>
          {MIGRATION_KIND_LABELS[file.kind]} — <span className="sub">{file.fileName}</span>
        </h4>
        {confirmed && <span className="pill">مؤكَّدة</span>}
      </header>
      <div className="mig-map-grid">
        {fields.map((field) => {
          const column = mapping[field.key] ?? null;
          const confidence =
            column !== null && file.mapping[field.key] === column
              ? score.get(field.key)
              : undefined;
          return (
            <label key={field.key} className={field.required && column === null ? 'missing' : ''}>
              <span>
                {field.label}
                {field.required && <span aria-label="مطلوب"> *</span>}
                {confidence !== undefined && (
                  <span className={'mig-score ' + (confidence >= 95 ? 'high' : 'mid')}>
                    اقتراح {confidence}٪
                  </span>
                )}
              </span>
              <select
                value={column ?? ''}
                onChange={(event) =>
                  setMapping({
                    ...mapping,
                    [field.key]: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              >
                <option value="">— غير مربوط —</option>
                {file.headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </div>
      {missing.length > 0 && (
        <p className="error">
          حقل مطلوب غير مربوط: {missing.map((field) => field.label).join('، ')} — يمكن تعبئته فى
          المراجعة.
        </p>
      )}
      <div className="table-scroll">
        <table className="mig-preview">
          <thead>
            <tr>
              {file.headers.map((header, index) => {
                const field = fields.find((item) => mapping[item.key] === index);
                return (
                  <th key={index}>
                    {header}
                    <span className={'mig-maps-to' + (field ? '' : ' none')}>
                      {field ? '← ' + field.label : 'لا يُستخدم'}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {file.preview.slice(0, 5).map((cells, row) => (
              <tr key={row}>
                {file.headers.map((_, index) => (
                  <td key={index}>{cells[index] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-row">
        <label className="grow">
          احفظ المطابقة «ملف مصدر» باسم (اختيارى)
          <input
            value={profileName}
            maxLength={80}
            placeholder="مثلاً: الأمين — عملاء"
            onChange={(event) => setProfileName(event.target.value)}
          />
          <Hint>احفظ ربط الأعمدة باسم، لتستخدمه مع أى ملف بنفس الشكل مستقبلاً.</Hint>
        </label>
        <div className="form-actions">
          <button
            type="button"
            className={confirmed && !changed ? 'ghost' : 'primary'}
            disabled={busy}
            onClick={() => void confirm()}
          >
            {confirmed && !changed ? 'أعد التأكيد' : 'تأكيد المطابقة'}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// المطابقة المحاسبية
// ---------------------------------------------------------------------------

const CHECK_ICON = { ok: 'check', fail: 'alert', warn: 'info', na: 'info' } as const;

function ReconcileStep({
  reconciliation,
  onSuspect,
}: {
  reconciliation: MigrationReconciliationResponse | null;
  onSuspect: (suspect: { kind: MigrationKind; rowId: string }) => void;
}) {
  if (!reconciliation) return <Loading />;
  return (
    <div className="mig-step">
      <p className={reconciliation.ready ? 'success' : 'error'} role="status">
        {reconciliation.ready
          ? 'كل المطابقات سليمة — الدفعة جاهزة للاعتماد.'
          : 'الاعتماد ممنوع حتى تُحل الفحوص الحمراء. اضغط أى صف مشتبه لتفتحه فى المراجعة.'}
      </p>
      <Totals reconciliation={reconciliation} />
      <ul className="mig-checks">
        {reconciliation.checks.map((check) => (
          <li key={check.code} className={'mig-check ' + check.status}>
            <Icon name={CHECK_ICON[check.status]} size={18} />
            <div className="grow">
              <strong>{check.title}</strong>
              <p>{check.detail}</p>
              {check.difference !== null && check.status === 'fail' && (
                <p className="mig-diff">
                  المتوقع {formatMoney(check.expected ?? '0')} — الفعلى{' '}
                  {formatMoney(check.actual ?? '0')} — الفرق{' '}
                  <strong>{formatMoney(check.difference)}</strong>
                </p>
              )}
              {check.suspects.length > 0 && (
                <div className="mig-suspects">
                  {check.suspects.map((suspect) => (
                    <button
                      key={suspect.rowId}
                      type="button"
                      className="link"
                      onClick={() => onSuspect(suspect)}
                    >
                      {suspect.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
